// "Pedido para X meses": prévia do pedido de compra de um fornecedor cobrindo
// os próximos X meses (fornecedores sazonais, ex.: papelaria no fim do ano).
// O cálculo é do ERPApi (RN-EST-75); aqui o usuário ajusta quantidades, marca
// os itens e abre o formulário de PC já preenchido.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, ShoppingCart, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Modal } from '@components/Dialog';
import { SimpleTable, TableCell, TableHeader, TableRow } from '@components/Table';
import { stockAnalyticsService } from '../../services/stockAnalyticsService';
import type { PurchasePlanHorizonResponse } from '../../types/stockAnalytics';
import { MetricInfoTooltip } from './MetricInfoTooltip';
import { HORIZON_MAX_MONTHS, HORIZON_MIN_MONTHS, buildPurchaseOrderPrefill, horizonUnitValue } from './horizonPurchaseOrderUtils';

const fmtInt = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString('pt-BR'));
const fmtDec = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
const fmtCurrency = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface Props {
  isOpen: boolean;
  onClose: () => void;
  providerId: number;
  providerName: string;
}

export const HorizonPurchaseOrderModal: React.FC<Props> = ({ isOpen, onClose, providerId, providerName }) => {
  const navigate = useNavigate();
  const [months, setMonths] = useState(3);
  const [preview, setPreview] = useState<PurchasePlanHorizonResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [selected, setSelected] = useState<Record<number, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await stockAnalyticsService.getPurchasePlanHorizon(providerId, months);
      setPreview(resp);
      const q: Record<number, number> = {};
      const s: Record<number, boolean> = {};
      resp.items.forEach((it) => {
        q[it.product_id] = it.suggested_qty;
        s[it.product_id] = it.suggested_qty > 0; // itens com zero vêm desmarcados
      });
      setQuantities(q);
      setSelected(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao calcular o pedido');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [providerId, months]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const items = useMemo(() => preview?.items ?? [], [preview]);
  const totals = useMemo(() => {
    let qty = 0;
    let value = 0;
    let count = 0;
    items.forEach((it) => {
      if (!selected[it.product_id]) return;
      const q = quantities[it.product_id] ?? 0;
      if (q <= 0) return;
      count++;
      qty += q;
      value += q * horizonUnitValue(it);
    });
    return { count, qty, value };
  }, [items, quantities, selected]);

  const toggleAll = () => {
    const allOn = items.length > 0 && items.every((it) => selected[it.product_id]);
    const next: Record<number, boolean> = {};
    items.forEach((it) => {
      next[it.product_id] = !allOn;
    });
    setSelected(next);
  };

  const openPurchaseOrder = () => {
    if (!preview) return;
    const prefill = buildPurchaseOrderPrefill(preview, items, quantities, selected);
    onClose();
    navigate('/purchase-orders/new', { state: { prefill } });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" showCloseButton={false}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-h3">Pedido para os próximos meses — {providerName}</h2>
          <p className="text-xs text-slate-500 mt-1">
            Quantidade = demanda prevista em (lead time + horizonte) + estoque de segurança − disponível − em pedido. Não é
            zerada por excesso: um disponível alto já leva a zero.
          </p>
        </div>
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600" aria-label="Fechar">
          <X size={18} />
        </button>
      </div>

      <div className="px-6 py-4 space-y-4 max-h-[65vh] overflow-y-auto custom-scrollbar">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Meses de cobertura</label>
            <input
              type="number"
              min={HORIZON_MIN_MONTHS}
              max={HORIZON_MAX_MONTHS}
              className="input w-28"
              value={months}
              onChange={(e) => {
                const v = Math.trunc(Number(e.target.value));
                if (Number.isFinite(v)) setMonths(Math.min(HORIZON_MAX_MONTHS, Math.max(HORIZON_MIN_MONTHS, v)));
              }}
            />
          </div>
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null} Recalcular
          </Button>
          {preview && (
            <span className="text-xs text-slate-500">
              Horizonte de {preview.horizon_days} dias · {fmtInt(preview.suggested_count)} de {fmtInt(preview.items_count)} itens com
              sugestão
            </span>
          )}
        </div>

        {error && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {loading && !preview && <p className="text-sm text-slate-500">Calculando…</p>}

        {preview && (
          <SimpleTable>
            <TableHeader>
              <tr>
                <th className="table-header th w-8">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300"
                    checked={items.length > 0 && items.every((it) => selected[it.product_id])}
                    onChange={toggleAll}
                    aria-label="Marcar todos"
                  />
                </th>
                <th className="table-header th">SKU</th>
                <th className="table-header th">Descrição</th>
                <th className="table-header th text-right">Físico</th>
                <th className="table-header th text-right">
                  <span className="inline-flex items-center gap-1">Disponível <MetricInfoTooltip metricKey="current_stock_available_qty" /></span>
                </th>
                <th className="table-header th text-right">Em pedido</th>
                <th className="table-header th text-right">
                  <span className="inline-flex items-center gap-1">Demanda no horizonte <MetricInfoTooltip metricKey="forecast_horizon_qty" /></span>
                </th>
                <th className="table-header th text-right">Quantidade</th>
                <th className="table-header th text-right">Preço unit.</th>
                <th className="table-header th text-right">Subtotal</th>
              </tr>
            </TableHeader>
            <tbody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-6">
                    <span className="text-sm text-slate-500">Este fornecedor não tem produtos na análise de estoque.</span>
                  </TableCell>
                </TableRow>
              )}
              {items.map((it) => {
                const q = quantities[it.product_id] ?? 0;
                const on = !!selected[it.product_id];
                const unit = horizonUnitValue(it);
                return (
                  <TableRow key={it.product_id} className={on ? '' : 'opacity-60'}>
                    <TableCell>
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={on}
                        onChange={(e) => setSelected({ ...selected, [it.product_id]: e.target.checked })}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                    <TableCell>
                      <span className="text-sm">{it.description ?? '—'}</span>
                      {it.avg_daily_demand === null && <span className="block text-[10px] text-slate-400">sem histórico de vendas</span>}
                    </TableCell>
                    <TableCell className="text-right">{fmtInt(it.current_stock_qty)}</TableCell>
                    <TableCell className="text-right">{fmtInt(it.current_stock_available_qty)}</TableCell>
                    <TableCell className="text-right">{fmtDec(it.on_order_qty, 0)}</TableCell>
                    <TableCell className="text-right">
                      {fmtDec(it.forecast_horizon_qty, 0)}
                      {it.safety_stock_qty > 0 && (
                        <span className="block text-[10px] text-slate-400">+ {fmtDec(it.safety_stock_qty, 0)} segurança</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <input
                        type="number"
                        min={0}
                        className="input w-24 text-right"
                        value={q}
                        onChange={(e) => {
                          const v = Math.max(0, Math.trunc(Number(e.target.value) || 0));
                          setQuantities({ ...quantities, [it.product_id]: v });
                          if (v > 0 && !on) setSelected({ ...selected, [it.product_id]: true });
                        }}
                      />
                      {q !== it.suggested_qty && (
                        <span className="block text-[10px] text-slate-400">sugerido {fmtInt(it.suggested_qty)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {unit > 0 ? (
                        <span title={it.provider_price ? 'Preço do fornecedor' : 'Sem preço de fornecedor — custo do produto'}>
                          {fmtCurrency(unit)}
                          {!it.provider_price && <span className="text-slate-400">*</span>}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">{on && q > 0 ? fmtCurrency(q * unit) : '—'}</TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </SimpleTable>
        )}
      </div>

      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm text-slate-600">
          {fmtInt(totals.count)} itens · {fmtInt(totals.qty)} un · <span className="font-semibold text-slate-800">{fmtCurrency(totals.value)}</span>
        </span>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={openPurchaseOrder} disabled={totals.count === 0} icon={<ShoppingCart size={16} />}>
            Abrir pedido de compra
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default HorizonPurchaseOrderModal;
